import { Logger } from "@paperbits/common/logging";
import { IObjectContractVisitor } from "./objectContractVisitor";
import { widgetName as customWidgetName } from "../../components/custom-widget/constants";
import { widgetName as customHtmlWidgetName } from "../../components/custom-html/constants";

interface ContentNode {
    type?: string;
    nodes?: ContentNode[] | string;
}

/**
 * Content-contract visitor that removes disabled widgets (Custom Widget and Custom HTML)
 * from page content when the portal editor runs for multitenant (SKUv2) services.
 *
 * Custom widgets and custom HTML are compiled out of the SKUv2 editor build through the
 * `#if !SkuV2` guards, so they can no longer be added from the editor. However, they can
 * still be injected into a page's content programmatically via the Management REST API
 * (contentTypes/contentItems). Because the widget types are not registered in the SKUv2
 * build, such nodes would otherwise fail to render (or leak a widget that is not supported
 * for multitenant). This visitor strips them from the content tree as it is read from
 * storage, ensuring they are never shown on the page in the editor.
 */
export class RemoveDisabledWidgetsVisitor implements IObjectContractVisitor {
    private readonly disabledWidgetTypes = new Set<string>([customWidgetName, customHtmlWidgetName]);

    constructor(private readonly logger?: Logger) { }

    public visit(contract: ContentNode): void {
        if (!contract || !Array.isArray(contract.nodes)) {
            return;
        }

        contract.nodes = contract.nodes.filter(node => {
            const isDisabled = this.disabledWidgetTypes.has(node?.type);

            if (isDisabled) {
                this.logger?.trackEvent("RemoveDisabledWidgetsVisitor", { message: `Removed disabled widget of type "${node?.type}" from content because it is not supported for multitenant (SKUv2).` });
            }

            return !isDisabled;
        });

        for (const node of contract.nodes) {
            this.visit(node);
        }
    }
}
